import { AuthService } from '@/services/AuthService';
import {
  IGetVendorOffersParams,
  IVendorOrderResponse,
  IUpdateVendorOrderOfferStatusParams,
  IVendorOrderItem
} from '@/types/IVendorOrderResponse';
import { api } from '@/utils/api';
import {
  CustomError,
  getErrorData,
  IGenericResponseRoot
} from '@/utils/getErrorData';
import { getHeaders } from '@/utils/getHeaders';
import axios from 'axios';
import { toast } from 'react-toastify';

export type AbortErrorType = 'Abort';

export interface IUpdateVendorOrderOfferVendorParams {
  order_offer_uuid: string;
  new_vendor_id: string;
  signal: AbortSignal;
}

export const getVendorOrderOffers = async (
  props: IGetVendorOffersParams
): Promise<IGenericResponseRoot<IVendorOrderResponse> | AbortErrorType> => {
  const {
    limit,
    offset,
    only_wait_action,
    order,
    delivery_from_city,
    planned_date_of_shipment,
    signal
  } = props;

  try {
    const headers: Record<string, string> = getHeaders();

    if (AuthService.authToken !== null) {
      headers.Authorization = `Bearer ${AuthService.authToken}`;
    } else {
      toast('Пользователь не авторизирован', { type: 'error' });
      throw new Error('Пользователь не авторизирован');
    }

    const params: Record<string, string | number | boolean> = {};
    if (limit !== undefined) params.limit = limit;
    if (offset !== undefined) params.offset = offset;
    if (only_wait_action !== undefined)
      params.only_wait_action = only_wait_action;
    if (order !== undefined && order !== '') params.order = order;
    if (delivery_from_city !== undefined && delivery_from_city !== '')
      params.delivery_from_city = delivery_from_city;
    if (
      planned_date_of_shipment !== undefined &&
      planned_date_of_shipment !== ''
    )
      params.planned_date_of_shipment = planned_date_of_shipment;

    const response = await api.get<IGenericResponseRoot<IVendorOrderResponse>>(
      '/admin/vendor-order/offers',
      {
        headers,
        params,
        signal
      }
    );

    if (response.data?.meta?.error !== null) {
      const extracted = getErrorData(response.data);
      throw new CustomError(extracted.message, extracted?.code);
    }

    if (response.status < 200 || response.status >= 300) {
      throw new CustomError(
        'Не удалось получить данные поставщиков для заказа',
        response?.status
      );
    }

    return response.data;
  } catch (error: unknown) {
    if (axios.isCancel(error)) {
      return 'Abort';
    }
    // eslint-disable-next-line no-console
    console.error('Ошибка при получении vendor-order/offers:', error);
    throw error;
  }
};

export const updateVendorOrderOfferStatus = async (
  props: IUpdateVendorOrderOfferStatusParams
): Promise<IGenericResponseRoot<IVendorOrderItem> | AbortErrorType> => {
  const { order_offer_uuid, status, signal } = props;

  try {
    const headers: Record<string, string> = getHeaders();

    if (AuthService.authToken !== null) {
      headers.Authorization = `Bearer ${AuthService.authToken}`;
    } else {
      toast('Пользователь не авторизирован', { type: 'error' });
      throw new Error('Пользователь не авторизирован');
    }

    const response = await api.put<IGenericResponseRoot<IVendorOrderItem>>(
      `/admin/vendor-order/offer/${order_offer_uuid}/status/${status}`,
      {},
      {
        headers,
        signal
      }
    );

    if (response.data?.meta?.error !== null) {
      const extracted = getErrorData(response.data);
      throw new CustomError(extracted.message, extracted?.code);
    }

    if (response.status < 200 || response.status >= 300) {
      throw new CustomError(
        'Не удалось обновить статус торгового предложения',
        response?.status
      );
    }

    return response.data;
  } catch (error: unknown) {
    if (axios.isCancel(error)) {
      return 'Abort';
    }
    // eslint-disable-next-line no-console
    console.error('Ошибка при обновлении статуса vendor-order/offer:', error);
    throw error;
  }
};

export const updateVendorOrderOfferVendor = async (
  props: IUpdateVendorOrderOfferVendorParams
): Promise<IGenericResponseRoot<IVendorOrderItem> | AbortErrorType> => {
  const { order_offer_uuid, new_vendor_id, signal } = props;

  try {
    const headers: Record<string, string> = getHeaders();

    if (AuthService.authToken !== null) {
      headers.Authorization = `Bearer ${AuthService.authToken}`;
    } else {
      toast('Пользователь не авторизирован', { type: 'error' });
      throw new Error('Пользователь не авторизирован');
    }

    const response = await api.put<IGenericResponseRoot<IVendorOrderItem>>(
      `/admin/vendor-order/offer/${order_offer_uuid}/vendor/${new_vendor_id}`,
      {},
      {
        headers,
        signal
      }
    );

    if (response.data?.meta?.error !== null) {
      const extracted = getErrorData(response.data);
      throw new CustomError(extracted.message, extracted?.code);
    }

    if (response.status < 200 || response.status >= 300) {
      throw new CustomError(
        'Не удалось обновить поставщика торгового предложения',
        response?.status
      );
    }

    return response.data;
  } catch (error: unknown) {
    if (axios.isCancel(error)) {
      return 'Abort';
    }
    // eslint-disable-next-line no-console
    console.error(
      'Ошибка при обновлении поставщика vendor-order/offer:',
      error
    );
    throw error;
  }
};
